'use client';

import { useEffect, useRef } from 'react';
import { useAuth } from '@/lib/use-auth';

// ═══════════════════════════════════════════════════════════════════════════
// AdSense Integration — Profileia
// Founder directive (2026-10-05): "empezar con adsense que no es tan invasiva"
//
// Publisher ID: pub-3406291655547373 (ca-pub-3406291655547373)
// Cuenta nueva creada el 2026-10-06 (cuenta anterior YouTube-only no servía
// para sitios web). Verificación de propiedad completada vía archivo HTML
// googlea5e98a3b7d345b24.html — ver frontend/public/.
//
// Tier gate: Solo usuarios tier='free' o 'basic' ven anuncios.
// Bronce+ no ven anuncios (incentivo de upgrade — modelo Spotify/YouTube).
// ═══════════════════════════════════════════════════════════════════════════

const ADSENSE_CLIENT_ID = 'ca-pub-3406291655547373';

// Tieres que VEN anuncios
const AD_SUPPORTED_TIERS = ['free', 'basic'];

/**
 * Hook que determina si el usuario actual debería ver anuncios.
 * Returns false si:
 *   - El usuario no está logueado (visitante público — SÍ ve anuncios en landing)
 *   - El usuario tiene tier Bronce+ (NO ve anuncios — incentivo de upgrade)
 */
export function useShouldShowAds(): boolean {
  const { user } = useAuth();

  // Visitantes no logueados ven anuncios en páginas públicas
  if (!user) return true;

  // Usuarios logueados: solo free y basic ven anuncios
  const tier = user.package?.tier ?? 'free';
  return AD_SUPPORTED_TIERS.includes(tier);
}

/**
 * AdSense Script Loader — se carga UNA vez al montar el primer <AdSlot>.
 * Inserta el script de AdSense en <head> si no existe ya.
 */
let scriptLoaded = false;

export function useAdSenseScript(): void {
  useEffect(() => {
    if (scriptLoaded) return;
    if (typeof window === 'undefined') return;

    const existing = document.querySelector(
      `script[src*="adsbygoogle.js"]`,
    );
    if (existing) {
      scriptLoaded = true;
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);
    scriptLoaded = true;
  }, []);
}

/**
 * <AdSlot> — Componente reutilizable para mostrar un anuncio de AdSense.
 *
 * Uso:
 *   <AdSlot slot="1234567890" format="auto" />
 *   <AdSlot slot="0987654321" format="rectangle" responsive={false} />
 *
 * El componente:
 *   1. Verifica si el usuario debería ver anuncios (tier gate)
 *   2. Carga el script de AdSense si es el primer <AdSlot> montado
 *   3. Inicializa (adsbygoogle = window.adsbygoogle || []).push({})
 *
 * Si el tier gate retorna false, el componente renderiza null (cero impacto visual).
 */
interface AdSlotProps {
  /** Google AdSense slot ID (from your AdSense dashboard) */
  slot?: string;
  /** Ad format: 'auto' (responsive), 'rectangle', 'horizontal', 'vertical' */
  format?: string;
  /** Whether the ad should be responsive (default true) */
  responsive?: boolean;
  /** CSS class for custom styling */
  className?: string;
  /** Minimum height to prevent layout shift while ad loads */
  minHeight?: number;
}

export function AdSlot({
  slot,
  format = 'auto',
  responsive = true,
  className = '',
  minHeight = 90,
}: AdSlotProps) {
  // PR #1456: if no slot ID provided, render nothing.
  // Auto ads (enabled in AdSense dashboard) handles ad injection
  // automatically via the adsbygoogle.js script in layout.tsx.
  // Manual <AdSlot> components are optional — when no slot ID is passed,
  // they render null (no broken/empty ad containers).
  if (!slot) return null;

  const shouldShow = useShouldShowAds();
  const adRef = useRef<HTMLModElement>(null);
  const pushed = useRef(false);

  useAdSenseScript();

  useEffect(() => {
    if (!shouldShow) return;
    if (pushed.current) return;
    if (typeof window === 'undefined') return;

    try {
      // @ts-expect-error — AdSense adds this to window at runtime
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch {
      // AdSense script not loaded yet — will retry on next render
    }
  }, [shouldShow]);

  // Don't render anything if user shouldn't see ads
  if (!shouldShow) return null;

  return (
    <div
      className={`ad-container ${className}`}
      style={{
        minHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '12px 0',
        overflow: 'hidden',
      }}
      aria-label="Publicidad"
    >
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', minHeight }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </div>
  );
}

/**
 * Variantes pre-configuradas para cada ubicación del sitio.
 * Estos componentes envuelven <AdSlot> con el formato correcto para cada posición.
 */

/** Banner horizontal 728x90 — para landing pública y conference exit */
export function AdBannerHorizontal({ slot }: { slot?: string }) {
  return (
    <AdSlot
      slot={slot}
      format="horizontal"
      responsive={false}
      minHeight={90}
      className="ad-banner-horizontal w-full max-w-[728px] mx-auto"
    />
  );
}

/** Card 300x250 — para sidebar del dashboard */
export function AdCardSidebar({ slot }: { slot?: string }) {
  return (
    <AdSlot
      slot={slot}
      format="rectangle"
      responsive={false}
      minHeight={250}
      className="ad-card-sidebar w-full max-w-[300px]"
    />
  );
}

/** Native ad — para entre productos del market */
export function AdNativeInline({ slot }: { slot?: string }) {
  return (
    <AdSlot
      slot={slot}
      format="fluid"
      responsive={true}
      minHeight={120}
      className="ad-native-inline w-full"
    />
  );
}
